import { useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { useAdminUsers, useAdminStats } from '../../hooks/useAdminApi';
import type { AdminUser } from '../../api/adminClient';

function timeAgo(ts: number): string {
  if (!ts) return 'never';
  const ms = Date.now() - ts * 1000;
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function truncateOwner(owner: string): string {
  if (owner.length <= 24) return owner;
  return owner.slice(0, 20) + '...';
}

export function AdminUsersPage() {
  const [offset, setOffset] = useState(0);
  const [prevPages, setPrevPages] = useState<AdminUser[]>([]);

  const usersQuery = useAdminUsers({ limit: 20, offset });
  const statsQuery = useAdminStats();

  const currentPage = usersQuery.data?.users ?? [];

  // Build display list: previous pages + current page
  const displayUsers = offset === 0
    ? currentPage
    : (() => {
        const existingOwners = new Set(prevPages.map(u => u.owner));
        const newUsers = currentPage.filter(u => !existingOwners.has(u.owner));
        return [...prevPages, ...newUsers];
      })();

  const stats = statsQuery.data;
  const total = usersQuery.data?.total ?? 0;

  const isInitialLoading = usersQuery.isLoading && offset === 0;

  const handleLoadMore = useCallback(() => {
    // Snapshot current display list before advancing offset
    setPrevPages(displayUsers);
    setOffset((prev) => prev + 20);
  }, [displayUsers]);

  if (isInitialLoading || statsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-[#8b949e] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold text-white">Users</h1>
        <p className="text-[13px] text-[#8b949e]">Activity and subscription status</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-1">TOTAL USERS</div>
          <div className="text-2xl font-bold text-white">{total}</div>
        </div>

        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-1">ACTIVE ALERTS</div>
          <div className="text-2xl font-bold text-white">{stats?.activeAlerts ?? 0}</div>
        </div>

        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-1">PRO SUBSCRIBERS</div>
          <div className="text-2xl font-bold text-emerald-500">{stats?.subscriptions?.pro ?? 0}</div>
        </div>

        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-1">NOTIFICATIONS 24H</div>
          <div className="text-2xl font-bold text-white">{stats?.notificationsSent24h ?? 0}</div>
        </div>
      </div>

      {/* Users list */}
      <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
        <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-3">USERS</div>

        <div className="space-y-2">
          {displayUsers.map((user) => {
            const isPro = user.subscriptionStatus === 'active';
            const channels = user.channels
              ? user.channels.split(',').filter(Boolean)
              : [];
            return (
              <div
                key={user.owner}
                className="flex items-center justify-between py-2 border-b border-[#21262d] last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-white truncate">
                    {truncateOwner(user.owner)}
                  </div>
                  <div className="text-[11px] text-[#8b949e]">
                    {user.alertCount} alerts
                    {channels.length > 0 ? ` · ${channels.join(', ')}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                      isPro
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-[#21262d] text-[#8b949e]'
                    }`}
                  >
                    {isPro ? 'Pro' : 'Free'}
                  </span>
                  <span className="text-[11px] text-[#8b949e]">
                    {timeAgo(user.lastActivity)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {displayUsers.length < total && (
          <button
            onClick={handleLoadMore}
            disabled={usersQuery.isFetching}
            className="mt-4 w-full py-2 text-[13px] text-[#8b949e] border border-[#21262d] rounded-lg hover:bg-[#21262d] transition-colors disabled:opacity-50"
          >
            {usersQuery.isFetching ? (
              <Loader2 className="w-4 h-4 animate-spin mx-auto" />
            ) : (
              'Load more'
            )}
          </button>
        )}
      </div>
    </div>
  );
}
